import { LoginResponse } from "../../core/ServerApiLayer/VOne/auth/login";
import { RoleEntityType } from "../../core/ServerApiLayer/VOne/auth/choose_role";

const ADMIN = "ADMIN";
const SUPERADMIN = "SUPERADMIN";

/*
An ADMIN role the user holds, flattened out of the login response's separate
institution/department lists so callers can treat the two scopes uniformly.

@key: A stable identity for the role, unique across both scopes (React keys).
@roleName: The role name, as `choose/role` expects it.
@entityType: Whether the role is held at an institution or at a department.
@entityId: The id of that institution or department.
@entityName: The display name of that institution or department.
 */
export interface AdminRole {
    key: string;
    roleName: string;
    entityType: RoleEntityType;
    entityId: number;
    entityName: string;
}

/*
What the admin panel should do with a user who has just authenticated.

@allowed: Login already scoped the session to an admin role — go straight in.
@select: Exactly one admin role — scope the session to it without prompting.
@choose: Several admin roles — the user must say which one they are acting as.
@denied: No admin role at all — they do not belong in the admin panel.
 */
export type AdminAccess =
    | { kind: "allowed" }
    | { kind: "select"; role: AdminRole }
    | { kind: "choose"; roles: AdminRole[] }
    | { kind: "denied" };

/*
Decide what the admin panel does with a freshly logged-in user.

Login is shared with the game, so it hands back *every* role the user holds and
declines to scope the session whenever there is more than one. Here only ADMIN
(at an institution or a department) and SUPERADMIN mean anything, so a
teacher-and-admin user must not be asked to choose between the two — the teacher
role is not an option this panel can offer. Filtering is therefore ours to do.

@loginResponse: The body of a successful login.

@returns: The AdminAccess decision — see the type above.
 */
export function resolveAdminAccess(loginResponse: LoginResponse): AdminAccess {
    const roles = loginResponse.roles;

    // Login only returns `roles` when it could not scope the session itself. If
    // it is absent the session is already scoped — to SUPERADMIN, or to the
    // user's single role, which may well be a TEACHER role we have to turn away.
    if (!roles) {
        const sessionRole = loginResponse.user_session.role;
        return sessionRole === SUPERADMIN || sessionRole === ADMIN
            ? { kind: "allowed" }
            : { kind: "denied" };
    }

    const adminRoles: AdminRole[] = [
        ...roles.institution_roles
            .filter((role) => role.role_name === ADMIN)
            .map((role) => ({
                key: `institution-${role.id}`,
                roleName: role.role_name,
                entityType: "institution" as RoleEntityType,
                entityId: role.institution_id,
                entityName: role.institution_name,
            })),
        ...roles.department_roles
            .filter((role) => role.role_name === ADMIN)
            .map((role) => ({
                key: `department-${role.id}`,
                roleName: role.role_name,
                entityType: "department" as RoleEntityType,
                entityId: role.department_id,
                entityName: role.department_name,
            })),
    ];

    if (adminRoles.length === 0) {
        return { kind: "denied" };
    }
    if (adminRoles.length === 1) {
        return { kind: "select", role: adminRoles[0] };
    }
    return { kind: "choose", roles: adminRoles };
}
